import { LoadingState } from '@lcs/shared/api';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   createRegisterAdapter,
   RegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import {
   extensionCallQueueCallsChanged,
   extensionCallQueueCompleted,
} from 'src/app/signalr/extension-call-queue-hub/extension-call-queue-hub.actions';
import { QueueHistoryFilterOptions } from '../models/queue-history-filter-option';
import { QueueHistoryRegisterOptions } from '../models/queue-history-register-options';
import { QueueHistoryRegisterListItem } from '../models/queue-history-register-results.model';
import * as queueHistoryRegisterActions from './queue-history-register.actions';

export const queueHistoryRegisterFeatureKey = 'queueHistoryRegister';

export interface QueueHistoryRegisterState
   extends RegisterState<QueueHistoryRegisterListItem, QueueHistoryRegisterOptions, OrderingOption> {
   filters: QueueHistoryFilterOptions[];
   queueExtensionId: number | null;
}

const adapter: RegisterAdapter<QueueHistoryRegisterListItem, QueueHistoryRegisterOptions, OrderingOption> =
   createRegisterAdapter<QueueHistoryRegisterListItem, QueueHistoryRegisterOptions, OrderingOption>({
      selectId: (item) => item.queueHistoryId ?? 0,
   });

const entityAdapter: EntityAdapter<QueueHistoryRegisterListItem> = createEntityAdapter<QueueHistoryRegisterListItem>({
   selectId: (item) => item.queueHistoryId,
   sortComparer: (a, b) => (b.queueHistoryId ?? 0) - (a.queueHistoryId ?? 0),
});

const defaultEnqueuedDateTimeOrdering = {
   field: 'enqueuedDateTime',
   direction: OrderingDirections.Asc,
};

const initialState: QueueHistoryRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   queueExtensionId: null,
   orderingOptions: [defaultEnqueuedDateTimeOrdering],
   callState: LoadingState.Init,
   quickFilters: null,
   endCursor: null,
   hasNextPage: false,
   totalCount: 0,
};

export const queueHistoryRegisterReducer = adapter.createReducer(
   queueHistoryRegisterActions.queueHistoryRegisterAdapterActions,
   initialState,
   on(queueHistoryRegisterActions.queueHistoryRegisterAdapterActions.pageEnter, (state) => ({
      ...state,
      callState: LoadingState.Loading,
   })),
   on(queueHistoryRegisterActions.queueHistoryRegisterInitialFilter, (state, action) => ({
      ...state,
      callState: LoadingState.Loading,
      filters: action.filters,
      queueExtensionId: action.queueExtensionId,
      orderingOptions: normalizeOrderingOptions(action.orderingOptions ?? state.orderingOptions),
   })),
   on(queueHistoryRegisterActions.queueHistoryRegisterAdapterActions.loadSuccess, (state) => {
      return reorderQueueHistoryRegisterState(state);
   }),
   on(queueHistoryRegisterActions.queueHistoryRegisterAdapterActions.loadPageSuccess, (state) => {
      return reorderQueueHistoryRegisterState(state);
   }),
   on(extensionCallQueueCallsChanged, (state, action) => {
      const queueHistoryId = Number(action.queueHistoryId);
      const queueExtensionId = Number(action.queueExtensionId);
      if (!Number.isFinite(queueHistoryId) || !Number.isFinite(queueExtensionId)) {
         return state;
      }

      const {
         callerIdName,
         callerIdNumber,
         callerStatus,
         agentExtension,
         queueExtension,
         callDetailId,
         startPosition,
         callCompleteDateTime,
         isCallBackRequested,
         callbackAttempts,
         enqueuedDateTime,
         answeredDateTime,
         serverName,
         callerChannelId,
         sipCallId,
         hasPreventEmptyQueue,
         waitTime,
      } = action;
      const callDispositionId = action.callDispositionId == null ? null : Number(action.callDispositionId);

      const exists = !!state.entities[queueHistoryId];
      const existingItem = state.entities[queueHistoryId];

      if (callCompleteDateTime) {
         if (!exists) return state;

         const newState = entityAdapter.removeOne(queueHistoryId, state);
         return {
            ...newState,
            totalCount: state.totalCount - 1,
         };
      }
      const isValidDisposition = callDispositionId === 5 || callDispositionId === null;

      if (!isValidDisposition) {
         if (exists) {
            const newState = entityAdapter.removeOne(queueHistoryId, state);
            return {
               ...newState,
               totalCount: state.totalCount - 1,
            };
         }

         return state;
      }

      const newItem: QueueHistoryRegisterListItem = {
         queueHistoryId,
         queueExtensionId,
         callerIdName: callerIdName ?? null,
         callerIdNumber: callerIdNumber ?? null,
         callerStatus,
         answeredDateTime: answeredDateTime ?? null,
         isCallbackRequested: isCallBackRequested ?? false,
         enqueuedDateTime: enqueuedDateTime ?? new Date(),
         callbackAttempts: callbackAttempts ?? 0,
         queueExtension: queueExtension ?? existingItem?.queueExtension,
         waitTime: waitTime,
         agentExtension: {
            ...agentExtension,
            queueExtensionId,
            callDetailId,
            startPosition,
         } as any,
         callDetailId: callDetailId,
         callerChannelId: callerChannelId,
         sipCallId: sipCallId,
         serverName: serverName,
         hasPreventEmptyQueue: hasPreventEmptyQueue,
      };

      if (!matchesCurrentRegisterFilters(state, newItem)) {
         if (!exists) {
            return state;
         }

         const newState = entityAdapter.removeOne(queueHistoryId, state);
         return {
            ...newState,
            totalCount: state.totalCount - 1,
         };
      }

      const updatedState = exists
         ? entityAdapter.updateOne({ id: queueHistoryId, changes: newItem }, state)
         : entityAdapter.addOne(newItem, state);

      return {
         ...reorderQueueHistoryRegisterState(updatedState),
         totalCount: state.totalCount + (exists ? 0 : 1),
      };
   }),

   on(extensionCallQueueCompleted, (state, action) => {
      const { queueHistoryId } = action;
      const exists = !!state.entities[queueHistoryId];

      if (!exists) {
         return state;
      }

      const newState = entityAdapter.removeOne(queueHistoryId, state);

      return {
         ...newState,
         totalCount: state.totalCount - 1,
      };
   }),
   on(queueHistoryRegisterActions.setOrderingOptionsFromSessionStorage, (state, action) => {
      return {
         ...state,
         orderingOptions: normalizeOrderingOptions(action.orderingOptions),
      };
   }),
   on(queueHistoryRegisterActions.queueHistoryRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(
      queueHistoryRegisterActions.clearQueueCallFailure,
      queueHistoryRegisterActions.sendToEmptyExitRouteFailure,
      (state, action) => {
         return {
            ...state,
            callState: LoadingState.Loaded,
         };
      }
   ),
   on(queueHistoryRegisterActions.queueHistoryRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      const newOrdering = buildOrderingOptions(state.orderingOptions, action.orderingOptions);

      const sorted = sortByOrderingOptions(
         Object.values(state.entities) as QueueHistoryRegisterListItem[],
         newOrdering
      );

      return {
         ...state,
         orderingOptions: newOrdering,
         callState: LoadingState.Loading,

         ids: sorted.map((x) => x.queueHistoryId),
         entities: sorted.reduce((acc, item) => {
            acc[item.queueHistoryId] = item;
            return acc;
         }, {} as Record<number, QueueHistoryRegisterListItem>),
      };
   })
);

export const queueHistoryRegisterSelector =
   createFeatureSelector<QueueHistoryRegisterState>(queueHistoryRegisterFeatureKey);

export const selectors = adapter.getSelectors(queueHistoryRegisterSelector);
export const { selectAll, selectCallState, selectTotalCount, selectEndCursor, selectQuickFilters } =
   adapter.getSelectors(queueHistoryRegisterSelector);

function sortByOrderingOptions(
   items: QueueHistoryRegisterListItem[],
   orderingOptions: OrderingOption[] | null
): QueueHistoryRegisterListItem[] {
   const normalizedOrderingOptions = normalizeOrderingOptions(orderingOptions);

   return [...items].sort((a, b) => {
      for (const opt of normalizedOrderingOptions) {
         const comparison = compareOrderingValues(
            getOrderingFieldValue(a, opt.field),
            getOrderingFieldValue(b, opt.field),
            opt.direction ?? defaultEnqueuedDateTimeOrdering.direction
         );

         if (comparison !== 0) {
            return comparison;
         }
      }

      const queueHistoryIdComparison = compareOrderingValues(
         a.queueHistoryId,
         b.queueHistoryId,
         OrderingDirections.Asc
      );
      if (queueHistoryIdComparison !== 0) {
         return queueHistoryIdComparison;
      }

      return 0;
   });
}

function buildOrderingOptions(
   existingOptions: OrderingOption[] | null,
   newOptions: OrderingOption[]
): OrderingOption[] {
   const existing = (existingOptions ?? []).filter((option) => option.direction !== OrderingDirections.NotSet);

   const additions = newOptions.filter(
      (option) =>
         option.direction !== OrderingDirections.NotSet &&
         !existing.some((existingOption) => existingOption.field === option.field)
   );

   const updates = existing.map((existingOption) => {
      return newOptions.find((newOption) => newOption.field === existingOption.field) ?? existingOption;
   });

   return normalizeOrderingOptions([...updates, ...additions]);
}

export function normalizeOrderingOptions(orderingOptions: OrderingOption[] | null | undefined): OrderingOption[] {
   const normalizedOrderingOptions = dedupeOrderingOptionsByField(orderingOptions ?? []).filter((option) => {
      return option.direction !== OrderingDirections.NotSet && option.field !== defaultEnqueuedDateTimeOrdering.field;
   });

   const enqueuedDateTimeOrdering =
      orderingOptions?.find((option) => option.field === defaultEnqueuedDateTimeOrdering.field) ??
      defaultEnqueuedDateTimeOrdering;

   return orderingOptions || [];
}

function dedupeOrderingOptionsByField(orderingOptions: OrderingOption[]): OrderingOption[] {
   const seenFields = new Set<string>();

   return orderingOptions.filter((option) => {
      if (seenFields.has(option.field)) {
         return false;
      }

      seenFields.add(option.field);
      return true;
   });
}

function compareOrderingValues(leftValue: unknown, rightValue: unknown, direction: OrderingDirections): number {
   const leftComparableValue = toComparableOrderingValue(leftValue);
   const rightComparableValue = toComparableOrderingValue(rightValue);
   const multiplier = direction === OrderingDirections.Asc ? 1 : -1;

   if (leftComparableValue === rightComparableValue) {
      return 0;
   }

   if (leftComparableValue == null) {
      return -1 * multiplier;
   }

   if (rightComparableValue == null) {
      return 1 * multiplier;
   }

   return leftComparableValue > rightComparableValue ? 1 * multiplier : -1 * multiplier;
}

function toComparableOrderingValue(value: unknown): number | string | boolean | null | undefined {
   if (value instanceof Date) {
      return value.getTime();
   }

   if (typeof value === 'string') {
      return value.toLocaleLowerCase();
   }

   if (typeof value === 'number' || typeof value === 'boolean' || value == null) {
      return value;
   }

   return undefined;
}

function getOrderingFieldValue(item: QueueHistoryRegisterListItem, field: string): any {
   switch (field) {
      case 'waitTime':
         return item.waitTime;
      case 'callerStatusDescription':
         return item.callerStatus;
      case 'agentExtensionNumber':
         return item.agentExtension?.number ?? null;
      default:
         break;
   }

   return field.split('.').reduce<any>((currentValue, fieldPart) => {
      if (currentValue == null || typeof currentValue !== 'object') {
         return undefined;
      }

      const record = currentValue as Record<string, unknown>;
      if (fieldPart in record) {
         return record[fieldPart];
      }

      const camelCaseFieldPart = fieldPart.charAt(0).toLowerCase() + fieldPart.slice(1);
      if (camelCaseFieldPart in record) {
         return record[camelCaseFieldPart];
      }

      const pascalCaseFieldPart = fieldPart.charAt(0).toUpperCase() + fieldPart.slice(1);
      if (pascalCaseFieldPart in record) {
         return record[pascalCaseFieldPart];
      }

      return undefined;
   }, item);
}

function getWaitTimeSortValue(item: QueueHistoryRegisterListItem): number | null {
   const enqueuedDateTime = toDateValue(item.enqueuedDateTime);
   if (enqueuedDateTime == null) {
      return null;
   }

   return Date.now() - enqueuedDateTime;
}

function toDateValue(value: Date | string | number | null | undefined): number | null {
   if (value == null) {
      return null;
   }

   if (value instanceof Date) {
      return value.getTime();
   }

   const numericValue = new Date(value).getTime();
   return Number.isFinite(numericValue) ? numericValue : null;
}

function matchesSelectedQueueFilter(filters: QueueHistoryFilterOptions[] | null, queueExtensionId: number): boolean {
   if (!filters || !filters.length) {
      return true;
   }

   const queueFilter = filters.find((filter) => filter.field === 'QueueExtensionId');
   if (!queueFilter) {
      return true;
   }

   if (queueFilter.operation === FilterOperations.EqualTo) {
      return queueFilter.value === queueExtensionId;
   }

   if (queueFilter.operation === FilterOperations.In && Array.isArray(queueFilter.value)) {
      return (queueFilter.value as number[]).includes(queueExtensionId);
   }

   return true;
}

function reorderQueueHistoryRegisterState<T extends QueueHistoryRegisterState>(state: T): T {
   const sorted = sortByOrderingOptions(getStateItems(state), state.orderingOptions);

   return {
      ...state,
      ids: sorted.map((item) => item.queueHistoryId),
      entities: sorted.reduce((acc, item) => {
         acc[item.queueHistoryId] = item;
         return acc;
      }, {} as Record<number, QueueHistoryRegisterListItem>),
   };
}

function getStateItems(state: QueueHistoryRegisterState): QueueHistoryRegisterListItem[] {
   return Object.values(state.entities).filter((item): item is QueueHistoryRegisterListItem => {
      return !!item;
   });
}

function matchesCurrentRegisterFilters(state: QueueHistoryRegisterState, item: QueueHistoryRegisterListItem): boolean {
   if (!matchesSelectedQueueFilter(state.filters, item.queueExtensionId ?? 0)) {
      return false;
   }

   return matchesQuickFilters(state.quickFilters, item);
}

function matchesQuickFilters(
   quickFilters: QueueHistoryRegisterOptions | null,
   item: QueueHistoryRegisterListItem
): boolean {
   const searchText = quickFilters?.searchText?.trim();
   if (!searchText) {
      return true;
   }

   const normalizedSearchText = searchText.toLocaleLowerCase();
   const searchFields = [item.queueExtension?.name, item.agentExtension?.name, item.callerIdName, item.callerIdNumber];

   if (
      searchFields.some((fieldValue) => {
         return fieldValue?.toLocaleLowerCase().includes(normalizedSearchText);
      })
   ) {
      return true;
   }

   const numericSearchText = searchText.match(/^\d+$/)?.[0];
   if (!numericSearchText) {
      return false;
   }

   const numericSearchFields = [item.queueExtension?.number, item.agentExtension?.number];
   return numericSearchFields.some((fieldValue) => {
      return fieldValue?.includes(numericSearchText);
   });
}
